import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StoreService } from "@services/store/StoreService";
import { OrganizationService } from "@services/organizations/OrganizationService";
import type {
    StoreCollectionSummary,
    StoreTagGroupSummary,
} from "@services/store/commands/cmdGetStorePage";
import { Paths } from "@/paths";
import CollectionCard from "./CollectionCard";
import StoreHero from "./StoreHero";
import { motiStoreCache } from "./cache";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import "./motiStore.scss";

export default function MotiStorePage() {
    useDocumentTitle("Moti Store");
    const navigate = useNavigate();
    const cached = motiStoreCache.getStorePage();

    const [collections, setCollections] = useState<StoreCollectionSummary[]>(cached?.collections ?? []);
    const [tagGroups, setTagGroups] = useState<StoreTagGroupSummary[]>(cached?.tagGroups ?? []);
    const [heroSrc, setHeroSrc] = useState<string>(cached?.heroSrc ?? "");
    const [isLoaded, setIsLoaded] = useState(!!cached);

    useEffect(() => {
        if (cached) return;

        const load = async () => {
            try {
                const defaultOrg = await new OrganizationService().getDefaultOrganization();
                if (!defaultOrg) return;
                const res = await new StoreService().getStorePage(String(defaultOrg.id), "Home");
                if (!res) return;
                const collections = res.collections ?? [];
                const tagGroups = res.tag_groups ?? [];
                const heroSrc = res.hero ?? "";
                setCollections(collections);
                setTagGroups(tagGroups);
                setHeroSrc(heroSrc);
                motiStoreCache.setStorePage({ orgId: defaultOrg.id, collections, tagGroups, heroSrc });
            } catch (err) {
                console.error("error loading store page", err);
            } finally {
                setIsLoaded(true);
            }
        };

        load();
    }, []);

    const base = `${import.meta.env.BASE_URL}${Paths.MOTI_STORE}`;

    return (
        <div className="moti-store">
            <div className="moti-store__title">Motimatic Store</div>

            <StoreHero src={heroSrc} isLoaded={isLoaded} />

            <div className="moti-store__section">
                <div className="moti-store__section-title">
                    <span className="title">Collections</span>
                    <span className="subtitle">Preselected content targeted at your sales funnel</span>
                </div>
                {isLoaded && collections.length === 0 ? (
                    <div className="moti-store__empty">No collections yet.</div>
                ) : (
                    <div className="moti-store__grid">
                        {collections.map((c) => (
                            <CollectionCard
                                key={c.id}
                                title={c.name}
                                ads={c.ads}
                                adCount={c.ad_count}
                                tagCount={c.tag_count}
                                onClick={() => navigate(`${base}/collection/${c.id}`)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {tagGroups.map((group) => (
                <div key={group.id} className="moti-store__section">
                    <div className="moti-store__section-title">
                        <span className="title">{group.name}</span>
                        <span className="subtitle">Preselected content targeted at motivational principles</span>
                    </div>
                    <div className="moti-store__grid">
                        {group.tags.map((tag) => (
                            <CollectionCard
                                key={tag.id}
                                title={tag.name}
                                ads={tag.ads}
                                adCount={tag.ad_count}
                                tagCount={tag.tag_count}
                                onClick={() => navigate(`${base}/tag/${tag.id}`)}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
